import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  FaArrowRight,
  FaChevronLeft,
  FaChevronRight,
  FaClock,
  FaEnvelope,
  FaSearch,
} from "react-icons/fa";
import BlogCard, { BlogImage, formatBlogDate } from "../../components/blog/BlogCard";
import { apiUrl } from "../../config/api";
import usePageMetadata from "../../hooks/usePageMetadata";
import {
  getInsuranceBlogCategories,
  getInsuranceBlogPosts,
} from "../../services/blogApi";
import "./Blog.css";

const PAGE_SIZE = 9;

const positivePage = (value) => {
  const page = Number.parseInt(value, 10);
  return Number.isFinite(page) && page > 0 ? page : 1;
};

const BlogNewsletter = () => {
  const [email, setEmail] = useState("");
  const [state, setState] = useState({ status: "idle", message: "" });

  const submit = async (event) => {
    event.preventDefault();
    setState({ status: "loading", message: "" });
    try {
      const response = await fetch(apiUrl("newsletter/"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (!response.ok) throw new Error("This email may already be subscribed.");
      setEmail("");
      setState({
        status: "success",
        message: "You're subscribed. Fresh insurance insight is on its way.",
      });
    } catch (error) {
      setState({
        status: "error",
        message: error?.message || "We could not subscribe you. Please try again.",
      });
    }
  };

  return (
    <section className="blog-newsletter" aria-labelledby="blog-newsletter-title">
      <div>
        <span className="blog-eyebrow blog-eyebrow--light">Stay informed</span>
        <h2 id="blog-newsletter-title">Useful ideas, delivered occasionally.</h2>
        <p>Get practical risk guidance, policy explainers, and Thrive insurance updates.</p>
      </div>
      <form onSubmit={submit}>
        <label htmlFor="blog-newsletter-email">Email address</label>
        <div>
          <FaEnvelope aria-hidden="true" />
          <input
            id="blog-newsletter-email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="you@example.com"
            autoComplete="email"
            required
          />
          <button type="submit" disabled={state.status === "loading"}>
            {state.status === "loading" ? "Joining…" : "Join the insights list"}
          </button>
        </div>
        <p
          className={`blog-newsletter__message blog-newsletter__message--${state.status}`}
          aria-live="polite"
        >
          {state.message || "No spam—just considered insurance guidance."}
        </p>
      </form>
    </section>
  );
};

const InsuranceBlog = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get("search")?.trim() || "";
  const category = searchParams.get("category")?.trim() || "";
  const page = positivePage(searchParams.get("page"));
  const [searchDraft, setSearchDraft] = useState(search);
  const [state, setState] = useState({ status: "loading", data: null, message: "" });
  const [categories, setCategories] = useState([]);
  const [featuredPost, setFeaturedPost] = useState(null);
  const [reload, setReload] = useState(0);

  const metadata = useMemo(
    () => ({
      title: "Thrive Insurance Insights | Practical Risk & Cover Guidance",
      description:
        "Explore practical insurance guidance, risk management ideas, policy explainers, and expert insight from Thrive Insurance Brokers Limited.",
      canonical: typeof window !== "undefined" ? `${window.location.origin}/blog` : "/blog",
      structuredData: {
        "@context": "https://schema.org",
        "@type": "Blog",
        name: "Thrive Insurance Insights",
        description: "Practical insurance and risk guidance from Thrive Insurance Brokers.",
        publisher: { "@type": "Organization", name: "Thrive Insurance Brokers Limited" },
      },
    }),
    []
  );
  usePageMetadata(metadata);

  useEffect(() => setSearchDraft(search), [search]);

  useEffect(() => {
    const controller = new AbortController();
    setState((current) => ({ ...current, status: "loading", message: "" }));
    getInsuranceBlogPosts(
      { page, page_size: PAGE_SIZE, q: search, category },
      controller.signal
    )
      .then((data) => setState({ status: "ready", data, message: "" }))
      .catch((error) => {
        if (error?.name !== "AbortError") {
          setState({ status: "error", data: null, message: error?.message });
        }
      });
    return () => controller.abort();
  }, [category, page, reload, search]);

  useEffect(() => {
    const controller = new AbortController();
    getInsuranceBlogCategories(controller.signal)
      .then((data) => setCategories(Array.isArray(data) ? data : data.results || []))
      .catch(() => setCategories([]));
    return () => controller.abort();
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    getInsuranceBlogPosts({ featured: true, page_size: 1 }, controller.signal)
      .then((data) => setFeaturedPost(data.results?.[0] || null))
      .catch(() => setFeaturedPost(null));
    return () => controller.abort();
  }, []);

  const updateFilters = (changes) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(changes).forEach(([key, value]) => {
      if (value) next.set(key, value);
      else next.delete(key);
    });
    if (!("page" in changes)) next.delete("page");
    setSearchParams(next);
  };

  const submitSearch = (event) => {
    event.preventDefault();
    updateFilters({ search: searchDraft.trim() });
  };

  const posts = state.data?.results || [];
  const showFeature = state.status === "ready" && page === 1 && !search && !category;
  const featured = showFeature
    ? featuredPost || posts.find((post) => post.is_featured) || posts[0]
    : null;
  const gridPosts = featured ? posts.filter((post) => post.id !== featured.id) : posts;
  const totalPages = Math.max(1, Math.ceil((state.data?.count || 0) / PAGE_SIZE));
  const filtered = Boolean(search || category);

  return (
    <main className="insurance-blog">
      <section className="blog-hero">
        <div className="blog-shell blog-hero__inner">
          <div>
            <span className="blog-eyebrow blog-eyebrow--light">Thrive Insurance Insights</span>
            <h1>Clear insurance knowledge for confident decisions.</h1>
            <p>
              Practical guidance, policy explainers, and risk insight from professionals
              who help people and businesses protect what matters.
            </p>
          </div>
          <div className="blog-hero__note" aria-label="Insights promise">
            <span>Curated by insurance professionals</span>
            <strong>Clear advice. Better-informed protection.</strong>
          </div>
        </div>
      </section>

      <div className="blog-shell blog-main-shell">
        {featured && (
          <section className="blog-feature" aria-labelledby="featured-insight-title">
            <Link className="blog-feature__media" to={`/blog/${featured.slug}`}>
              <BlogImage post={featured} eager />
            </Link>
            <div className="blog-feature__copy">
              <span className="blog-kicker">Featured insight · {featured.category}</span>
              <h2 id="featured-insight-title">
                <Link to={`/blog/${featured.slug}`}>{featured.title}</Link>
              </h2>
              <p>{featured.excerpt}</p>
              <div className="blog-feature__meta">
                <span>{featured.author_name || "Thrive Insurance"}</span>
                <time dateTime={featured.published_at}>{formatBlogDate(featured.published_at)}</time>
                <span><FaClock aria-hidden="true" /> {featured.read_time_minutes || 1} min read</span>
              </div>
              <Link className="blog-button" to={`/blog/${featured.slug}`}>
                Read the insight <FaArrowRight aria-hidden="true" />
              </Link>
            </div>
          </section>
        )}

        <section className="blog-discovery" aria-labelledby="stories-title">
          <div className="blog-discovery__top">
            <div>
              <span className="blog-eyebrow">Explore our insights</span>
              <h2 id="stories-title">{filtered ? "Insights for your search" : "Latest insights"}</h2>
            </div>
            <form className="blog-search" role="search" onSubmit={submitSearch}>
              <label className="blog-sr-only" htmlFor="blog-search-input">Search insurance insights</label>
              <FaSearch aria-hidden="true" />
              <input
                id="blog-search-input"
                type="search"
                value={searchDraft}
                onChange={(event) => setSearchDraft(event.target.value)}
                placeholder="Search cover, claims, risk…"
              />
              <button type="submit">Search</button>
            </form>
          </div>

          <div className="blog-category-row" aria-label="Filter stories by category">
            <button
              type="button"
              className={!category ? "is-active" : ""}
              aria-pressed={!category}
              onClick={() => updateFilters({ category: "" })}
            >
              All stories
            </button>
            {categories.map((item) => {
              const name = typeof item === "string" ? item : item.name;
              const count = typeof item === "string" ? null : item.count ?? item.post_count;
              return (
                <button
                  type="button"
                  key={name}
                  className={category === name ? "is-active" : ""}
                  aria-pressed={category === name}
                  onClick={() => updateFilters({ category: name })}
                >
                  {name}{count !== null && count !== undefined ? ` (${count})` : ""}
                </button>
              );
            })}
          </div>

          {filtered && (
            <div className="blog-active-filter">
              <p>
                Showing {state.data?.count || 0} result{state.data?.count === 1 ? "" : "s"}
                {search ? <> for <strong>“{search}”</strong></> : null}
                {category ? <> in <strong>{category}</strong></> : null}
              </p>
              <button type="button" onClick={() => setSearchParams({})}>Clear filters</button>
            </div>
          )}

          {state.status === "loading" && (
            <div className="blog-grid" aria-label="Loading insurance insights">
              {Array.from({ length: 6 }, (_, index) => (
                <div className="blog-card-skeleton" key={index} />
              ))}
            </div>
          )}

          {state.status === "error" && (
            <div className="blog-state" role="alert">
              <span className="blog-state__icon">!</span>
              <h3>We could not load insurance insights</h3>
              <p>{state.message || "Please check your connection and try again."}</p>
              <button className="blog-button" type="button" onClick={() => setReload((value) => value + 1)}>
                Try again
              </button>
            </div>
          )}

          {state.status === "ready" && !posts.length && (
            <div className="blog-state" role="status">
              <FaSearch className="blog-state__search-icon" aria-hidden="true" />
              <h3>No stories match those details</h3>
              <p>Try a broader search or explore all insurance insights.</p>
              <button className="blog-button" type="button" onClick={() => setSearchParams({})}>
                View all stories
              </button>
            </div>
          )}

          {state.status === "ready" && gridPosts.length > 0 && (
            <div className="blog-grid">
              {gridPosts.map((post) => <BlogCard post={post} key={post.id || post.slug} />)}
            </div>
          )}

          {state.status === "ready" && totalPages > 1 && (
            <nav className="blog-pagination" aria-label="Blog pagination">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => updateFilters({ page: String(page - 1) })}
              >
                <FaChevronLeft aria-hidden="true" /> Previous
              </button>
              <span>Page {page} of {totalPages}</span>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => updateFilters({ page: String(page + 1) })}
              >
                Next <FaChevronRight aria-hidden="true" />
              </button>
            </nav>
          )}
        </section>

        <BlogNewsletter />
      </div>
    </main>
  );
};

export default InsuranceBlog;
